import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useForm } from 'hooks/useForm';
import { updateGroup } from '@redux/features/groupsSlice';
import SessionStorageUtils from 'utils/SessionStorageUtils';
import { groupSchema } from 'constants/formValidationSchemas/groupSchema';
import { SessionStorageKeys } from 'constants/sessionStorageKeys';
import { Dropdown } from 'components/Dropdown';
import { Checkbox } from 'components/Checkbox';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { Input } from 'components/Input';
import styles from './GroupPage.scss';

export const EditGroup = ({ group, isOpen, setIsOpen }) => {
  const currentLocation = SessionStorageUtils.getData(SessionStorageKeys.LOCATION);
  const { isLoading, error } = useSelector((state) => state.groups);
  const { locations } = useSelector((state) => state.locations);
  const dispatch = useDispatch();

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
    resetForm,
    setFieldValue,
  } = useForm({
    initialValues: {
      name: group.name,
      isClub: group.isClub,
      locationID: group.location.id,
    },
    validationSchema: groupSchema,
    onSubmit: async (values) => {
      const payload = {
        id: group.id,
        ...values,
        locationID: Number(values.locationID),
      };

      const result = await dispatch(updateGroup(payload));

      if (result.meta?.requestStatus === 'fulfilled') {
        resetForm();
        setIsOpen(false);
      }
    },
    status: {
      isLoading,
      error,
    },
  });

  useEffect(() => {
    if (isOpen) {
      resetForm({
        values: {
          name: group.name,
          isClub: group.isClub,
          locationID: group.location.id,
        },
      });
    }
  }, [isOpen, group]);

  const locationsOptions = useMemo(
    () => locations.map((location) => ({ label: location.name, value: location.id })),
    [locations]
  );

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <h3 className={styles.modalTitle}>Редагувати групу</h3>
      <form className={styles.editForm} onSubmit={handleSubmit}>
        {currentLocation?.admin && (
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Локація групи</label>
            <Dropdown
              className={styles.field}
              options={locationsOptions}
              placeholder='Оберіть локацію'
              value={locationsOptions.find((opt) => opt.value === values.locationID)}
              onChange={(option) => setFieldValue('locationID', option?.value || null)}
              errorText={touched.locationID && errors.locationID}
              invalid={touched.locationID && !!errors.locationID}
              onBlur={() => handleBlur({ target: { name: 'locationID' } })}
              clearable={false}
            />
          </div>
        )}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Назва групи</label>
          <Input
            className={styles.field}
            value={values.name}
            name='name'
            placeholder='Назва групи'
            errorText={touched.name && errors.name}
            invalid={touched.name && !!errors.name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <Checkbox
          className={styles.field}
          label='Група клубу "M.U.ST Dance&Win"'
          checked={values.isClub}
          onChange={(e) => setFieldValue('isClub', e.target.checked)}
          id='isClub-checkbox'
        />
        <div className={styles.formActions}>
          <Button onClick={() => setIsOpen(false)} ghostStyle no3D smallStyle>
            Скасувати
          </Button>
          <Button type='submit' disabled={!isValid || isSubmitting} no3D smallStyle>
            Зберегти
          </Button>
        </div>
      </form>
    </Modal>
  );
};
